import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Tabela from "../../components/Tabela";
import { Tipo, buscaUsuarios, todoUsuario } from "../../types/usuarios";
import {
  deleteUsuario,
  getUsuarioById,
  getUsuarios,
  ativaUsuario,
} from "../../api/usuariosApi";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Usuarios: React.FC = () => {
  const [usuarios, setUsuarios] = useState<buscaUsuarios[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [usuarioSelecionado, setUsuarioSelecionado] =
    useState<todoUsuario | null>(null);
  const [modalAberta, setModalAberta] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsuarios = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("Token não encontrado");
        }
        const usuariosData = await getUsuarios(token);
        setUsuarios(usuariosData);
      } catch (error) {
        toast.error("Erro ao buscar usuários!");
        console.error("Erro ao buscar usuários:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsuarios();
  }, []);

  const handleAddUsuario = () => {
    navigate("/adicionarUsuario");
  };

  const handleEditUsuario = (id: string) => {
    navigate(`/editarUsuario/${id}`);
  };

  const handleDeleteUsuario = async (id: string) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Token não encontrado");
      }
      await deleteUsuario(token, id);
      setUsuarios(usuarios.filter((usuario) => usuario.id !== Number(id)));
      toast.success("Usuário desativado com sucesso!");
    } catch (error) {
      toast.error("Erro ao desativar Usuário!");
      console.error("Erro ao desativar Usuário:", error);
    }
  };

  const handleViewUsuario = async (id: string) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Token não encontrado");
      }
      const usuarioData = await getUsuarioById(token, id);
      setUsuarioSelecionado(usuarioData);
      setModalAberta(true);
    } catch (error) {
      toast.error("Erro ao carregar detalhes do usuário!");
      console.error("Erro ao carregar detalhes do usuário:", error);
      throw error;
    }
  };

  const handleFecharModal = () => {
    setModalAberta(false);
  };

  const handleActivateUsuario = async (id: string) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Token não encontrado");
      }
      await ativaUsuario(token, id);
      const usuariosAtualizados = usuarios.map((usuario) =>
        usuario.id === Number(id)
          ? { ...usuario, ativo_usuario: true }
          : usuario
      );
      setUsuarios(usuariosAtualizados);
      toast.success("Usuário ativado com sucesso!");
    } catch (error) {
      toast.error("Erro ao ativar Usuário!");
      console.error("Erro ao ativar Usuário:", error);
    }
  };

  const colunas = [
    { id: "id", label: "ID" },
    { id: "nome_usuario", label: "Nome do Usuário" },
    { id: "login_usuario", label: "Login" },
    {
      id: "is_admin",
      label: "Admin",
      render: (value: boolean) => (value ? "Sim" : "Não"),
    },
    { id: "empresa.nome_empresa", label: "Empresa" },
    {
      id: "ativo_usuario",
      label: "Ativo",
      render: (value: boolean) => (value ? "Sim" : "Não"),
    },
  ];

  const dadosConvertidos = usuarios.map((usuario) => ({
    ...usuario,
    is_admin: usuario.is_admin ? "Sim" : "Não",
    ativo_usuario: usuario.ativo_usuario ? "Sim" : "Não",
  }));

  return (
    <Box sx={{ p: 3 }}>
      <Tabela
        titulo="Usuários"
        colunas={colunas}
        dados={dadosConvertidos}
        onAdd={handleAddUsuario}
        onEdit={handleEditUsuario}
        onView={handleViewUsuario}
        onDelete={handleDeleteUsuario}
        onActivate={handleActivateUsuario}
        showVisibilityIcon={true}
        loading={loading}
      />
      <Modal open={modalAberta} onClose={handleFecharModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            minWidth: "50%",
          }}
        >
          {usuarioSelecionado ? (
            <>
              <Typography variant="h6">Empresa Cadastrada:</Typography>
              {usuarioSelecionado.empresa ? (
                <TableContainer component={Box} sx={{ maxHeight: 400 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>CNPJ Empresa</TableCell>
                        <TableCell>Nome da Empresa</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>{usuarioSelecionado.empresa.id}</TableCell>
                        <TableCell>
                          {usuarioSelecionado.empresa.cnpj_empresa}
                        </TableCell>
                        <TableCell>
                          {usuarioSelecionado.empresa.nome_empresa}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body1" mt={2}>
                  Nenhuma empresa encontrada.
                </Typography>
              )}
              <Typography variant="h6" mt={4}>
                Tipos Cadastrados:
              </Typography>
              {usuarioSelecionado.tipo.length > 0 ? (
                <TableContainer component={Box} sx={{ maxHeight: 400 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Tipo Usuário</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {usuarioSelecionado.tipo.map((tipo: Tipo) => (
                        <TableRow key={tipo.id}>
                          <TableCell>{tipo.id}</TableCell>
                          <TableCell>{tipo.nome_tipo}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body1" mt={2}>
                  Nenhum tipo encontrado.
                </Typography>
              )}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  onClick={handleFecharModal}
                >
                  Fechar
                </Button>
              </Box>
            </>
          ) : (
            <Typography variant="body1">
              Nenhuma informação disponível.
            </Typography>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default Usuarios;
